import { useEffect, useRef } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose?: () => void;
}

const toastStyles = `
  @keyframes scaleInCenter {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
  @keyframes scaleOutCenter {
    from {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
  }
  .toast-container {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 9999;
    min-width: max-content;
    max-width: 24rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    animation: scaleInCenter 0.3s ease-out forwards;
  }
  .toast-container.closing {
    animation: scaleOutCenter 0.3s ease-in forwards;
  }
`;

export default function Toast({ message, type = 'info', duration = 3000, onClose }: ToastProps) {
  const toastRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();

  const bgColor = {
    success: 'bg-green-50',
    error: 'bg-red-50',
    info: 'bg-blue-50',
  }[type];

  const textColor = {
    success: 'text-green-800',
    error: 'text-red-800',
    info: 'text-blue-800',
  }[type];

  const iconColor = {
    success: 'text-green-600',
    error: 'text-red-600',
    info: 'text-blue-600',
  }[type];

  const icons = {
    success: <CheckCircleIcon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />,
    error: <ExclamationTriangleIcon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />,
    info: <InformationCircleIcon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />,
  };

  useEffect(() => {
    if (duration > 0) {
      closeTimeoutRef.current = setTimeout(() => {
        if (toastRef.current) {
          toastRef.current.classList.add('closing');
          setTimeout(() => {
            onClose?.();
          }, 300);
        }
      }, duration);
    }

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [duration, onClose]);

  const handleClose = () => {
    if (toastRef.current) {
      toastRef.current.classList.add('closing');
      setTimeout(() => {
        onClose?.();
      }, 300);
    }
  };

  return (
    <>
      <style>{toastStyles}</style>
      <div
        ref={toastRef}
        className={`toast-container ${bgColor}`}
      >
        {icons[type]}
        <span className={`text-sm font-medium whitespace-nowrap ${textColor}`}>{message}</span>
        <button
          onClick={handleClose}
          className={`ml-1 ${textColor} hover:opacity-70 transition-opacity flex-shrink-0`}
        >
          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </>
  );
}
